import { authService } from './authService';

/** API origin from build/env (`REACT_APP_API_URL`). Local fallback only when unset. */
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

class ApiService {
  /**
   * Anonymous readiness probe — used to gate the login screen until the API can serve traffic.
   * Returns true when /health/ready responds OK; throws otherwise.
   */
  async checkApiReady() {
    const response = await fetch(`${API_BASE_URL}/health/ready`, {
      method: 'GET',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`API not ready (HTTP ${response.status})`);
    }

    return true;
  }

  async makeRequest(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    
    try {
      if (authService.isAuthenticated()) {
        return await authService.makeAuthenticatedRequest(url, options);
      }

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      });

      if (!response.ok) {
        const error = new Error(`HTTP error! status: ${response.status}`);
        error.status = response.status;
        throw error;
      }

      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      if (error?.status !== 404 && error?.status !== 403) {
        console.error('API request failed:', error);
      }
      throw error;
    }
  }

  // Ticket endpoints
  async getTickets(status = null, categoryId = null, teamId = null) {
    const params = new URLSearchParams();
    if (status !== null) params.append('status', status);
    if (categoryId !== null) params.append('categoryId', categoryId);
    if (teamId !== null) params.append('teamId', teamId);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.makeRequest(`/api/tickets${query}`);
  }

  async getTicketById(id) {
    return this.makeRequest(`/api/tickets/${id}`);
  }

  async createTicket(ticketData) {
    const enhancedTicketData = {
      ...ticketData,
      userId: authService.getUserId(),
      userName: authService.getUserName(),
      userEmail: authService.getUserEmail()
    };

    return this.makeRequest('/api/tickets', {
      method: 'POST',
      body: JSON.stringify(enhancedTicketData)
    });
  }

  async createExergyIssue(issueData) {
    const payload = await this.makeRequest('/api/exergy-issues', {
      method: 'POST',
      body: JSON.stringify(issueData)
    });
    return payload;
  }

  async deleteTicket(id) {
    return this.makeRequest(`/api/tickets/${id}`, { method: 'DELETE' });
  }

  // Category endpoints
  async getCategories() {
    return this.makeRequest('/api/categories');
  }

  async createCategory(data) {
    return this.makeRequest('/api/categories', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateCategory(id, data) {
    return this.makeRequest(`/api/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteCategory(id) {
    return this.makeRequest(`/api/categories/${id}`, {
      method: 'DELETE'
    });
  }

  // Request Type endpoints
  async getRequestTypes() {
    return this.makeRequest('/api/request-types');
  }

  async createRequestType(data) {
    return this.makeRequest('/api/request-types', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateRequestType(id, data) {
    return this.makeRequest(`/api/request-types/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteRequestType(id) {
    return this.makeRequest(`/api/request-types/${id}`, {
      method: 'DELETE'
    });
  }

  // SLA Report endpoints (use /api/sla/report so teamId filter is applied)
  async getSlaReport(teamId = null) {
    const query = (teamId != null && teamId !== '') ? `?teamId=${teamId}` : '';
    return this.makeRequest(`/api/sla/report${query}`);
  }

  async getTicketChurnReport(startDate, endDate, teamId = null) {
    const params = new URLSearchParams({
      startDate: startDate,
      endDate: endDate
    });
    if (teamId !== null) params.append('teamId', teamId);
    return this.makeRequest(`/api/tickets/churn-report?${params}`);
  }

  // Export methods
  async _authHeaders() {
    const headers = {};
    if (authService.isAuthenticated()) {
      const token = await authService.getAccessToken();
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  async exportSlaReportToExcel() {
    const url = `${API_BASE_URL}/api/export/sla-report/excel`;
    const response = await fetch(url, {
      method: 'GET',
      headers: await this._authHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to export SLA report to Excel');
    }

    const blob = await response.blob();
    return blob;
  }

  async exportTicketChurnReportToExcel(startDate, endDate) {
    const params = new URLSearchParams({
      startDate: startDate,
      endDate: endDate
    });
    const url = `${API_BASE_URL}/api/export/ticket-churn-report/excel?${params}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: await this._authHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to export Ticket Churn report to Excel');
    }

    const blob = await response.blob();
    return blob;
  }

  async exportAssetsToExcel() {
    const url = `${API_BASE_URL}/api/export/assets/excel`;
    const response = await fetch(url, {
      method: 'GET',
      headers: await this._authHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to export assets to Excel');
    }

    const blob = await response.blob();
    return blob;
  }



  // SLA Rule Management endpoints
  async getSlaRules() {
    return this.makeRequest('/api/sla/rules');
  }

  async createSlaRule(ruleData) {
    return this.makeRequest('/api/sla/rules', {
      method: 'POST',
      body: JSON.stringify(ruleData)
    });
  }

  async updateSlaRule(id, ruleData) {
    return this.makeRequest(`/api/sla/rules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(ruleData)
    });
  }

  async deleteSlaRule(id) {
    return this.makeRequest(`/api/sla/rules/${id}`, { method: 'DELETE' });
  }

  // User management endpoints
  async getUsers() {
    return this.makeRequest('/api/users');
  }

  // Ticket Response endpoints
  async getTicketResponses(ticketId, userRole = null) {
    return this.makeRequest(`/api/ticket-responses/ticket/${ticketId}`);
  }

  async createTicketResponse(ticketId, responseData) {
    return this.makeRequest(`/api/ticket-responses/ticket/${ticketId}`, {
      method: 'POST',
      body: JSON.stringify(responseData)
    });
  }

  async createUser(userData) {
    return this.makeRequest('/api/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async registerUser() {
    // Access tokens for the custom API often omit email; send Graph/MSAL profile as a hint.
    // API still binds User.Id from the JWT and enforces allowed email domains.
    return this.makeRequest('/api/users/register', {
      method: 'POST',
      body: JSON.stringify({
        id: authService.getUserId() || '',
        name: authService.getUserName() || '',
        email: authService.getUserEmail() || '',
      }),
    });
  }

  async getUserById(userId) {
    return this.makeRequest(`/api/users/${userId}`);
  }

  async updateUserRole(userId, role) {
    return this.makeRequest(`/api/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role })
    });
  }

  // Attachment endpoints
  async getAttachmentsByTicket(ticketId) {
    return this.makeRequest(`/api/attachments/ticket/${ticketId}`);
  }

  async uploadAttachment(ticketId, file, description = null) {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }

    const url = `${API_BASE_URL}/api/attachments/${ticketId}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: await this._authHeaders(),
      body: formData
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  async downloadAttachment(attachmentId) {
    const url = `${API_BASE_URL}/api/attachments/${attachmentId}/download`;
    const response = await fetch(url, { headers: await this._authHeaders() });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.blob();
  }

  async deleteAttachment(attachmentId) {
    return this.makeRequest(`/api/attachments/${attachmentId}`, { method: 'DELETE' });
  }

  // Report endpoints
  async getTicketCategoryBreakdown(startDate = null, endDate = null, teamId = null) {
    const params = new URLSearchParams();

    if (startDate) params.append('startDate', startDate.toISOString().split('T')[0]);
    if (endDate) params.append('endDate', endDate.toISOString().split('T')[0]);
    if (teamId !== null) params.append('teamId', teamId);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.makeRequest(`/api/reports/tickets/categories${query}`);
  }

  // Asset Management endpoints
  async getAssets() {
    return this.makeRequest('/api/assets');
  }

  async createAsset(assetData) {
    return this.makeRequest('/api/assets', {
      method: 'POST',
      body: JSON.stringify(assetData)
    });
  }

  async updateAsset(id, assetData) {
    return this.makeRequest(`/api/assets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(assetData)
    });
  }

  async deleteAsset(id) {
    return this.makeRequest(`/api/assets/${id}`, { method: 'DELETE' });
  }

  // Email Template methods
  async getCategoryEmailTemplates() {
    return this.makeRequest('/api/category-email-templates');
  }

  async createOrUpdateCategoryEmailTemplate(templateData) {
    return this.makeRequest('/api/category-email-templates', {
      method: 'POST',
      body: JSON.stringify(templateData)
    });
  }

  async deleteCategoryEmailTemplate(categoryName) {
    return this.makeRequest(`/api/category-email-templates/${encodeURIComponent(categoryName)}`, {
      method: 'DELETE'
    });
  }

  // Category notification recipients stored in SystemSettings
  async getCategoryNotificationEmails(categoryName) {
    const key = `CategoryNotifications.${categoryName}`;
    const result = await this.makeRequest(`/api/SystemSettings/${encodeURIComponent(key)}`);
    return result?.value || '';
  }

  async setCategoryNotificationEmails(categoryName, emails) {
    const key = `CategoryNotifications.${categoryName}`;
    return this.makeRequest(`/api/SystemSettings/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify(emails || '')
    });
  }

  // Teams endpoints
  async getTeams(userId = null) {
    let endpoint = '/api/teams';
    if (userId) endpoint += `?userId=${encodeURIComponent(userId)}`;
    return this.makeRequest(endpoint);
  }

  async getTeamById(id) {
    return this.makeRequest(`/api/teams/${id}`);
  }

  async createTeam(teamData) {
    return this.makeRequest('/api/teams', {
      method: 'POST',
      body: JSON.stringify(teamData)
    });
  }

  async updateTeam(id, teamData) {
    return this.makeRequest(`/api/teams/${id}`, {
      method: 'PUT',
      body: JSON.stringify(teamData)
    });
  }

  async deleteTeam(id) {
    return this.makeRequest(`/api/teams/${id}`, { method: 'DELETE' });
  }

  // Admin Center endpoints
  async getAdminCenters() {
    return this.makeRequest('/api/admincenters');
  }

  async createAdminCenter(data) {
    return this.makeRequest('/api/admincenters', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateAdminCenter(id, data) {
    return this.makeRequest(`/api/admincenters/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteAdminCenter(id) {
    return this.makeRequest(`/api/admincenters/${id}`, { method: 'DELETE' });
  }

  // Supplier endpoints
  async getSuppliers() {
    return this.makeRequest('/api/suppliers');
  }

  async createSupplier(data) {
    return this.makeRequest('/api/suppliers', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateSupplier(id, data) {
    return this.makeRequest(`/api/suppliers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteSupplier(id) {
    return this.makeRequest(`/api/suppliers/${id}`, { method: 'DELETE' });
  }

  // Supplier contract endpoints
  async getSupplierContracts(supplierId) {
    return this.makeRequest(`/api/suppliercontracts/supplier/${supplierId}`);
  }

  async createSupplierContract(data) {
    return this.makeRequest('/api/suppliercontracts', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateSupplierContract(id, data) {
    return this.makeRequest(`/api/suppliercontracts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteSupplierContract(id) {
    return this.makeRequest(`/api/suppliercontracts/${id}`, { method: 'DELETE' });
  }

  // Supplier invoice endpoints
  async getSupplierInvoices(supplierId) {
    return this.makeRequest(`/api/supplierinvoices/supplier/${supplierId}`);
  }

  async createSupplierInvoice(formData) {
    // formData is a FormData instance — do NOT set Content-Type; browser sets multipart boundary
    const url = `${API_BASE_URL}/api/supplierinvoices`;
    let headers = {};
    if (authService.isAuthenticated()) {
      const token = await authService.getAccessToken();
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(url, { method: 'POST', headers, body: formData });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  }

  async updateSupplierInvoice(id, data) {
    return this.makeRequest(`/api/supplierinvoices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteSupplierInvoice(id) {
    return this.makeRequest(`/api/supplierinvoices/${id}`, { method: 'DELETE' });
  }

  // Claude Usage Dashboard endpoints
  async getClaudeUsageSummary(days = 30) {
    return this.makeRequest(`/api/claudeusage/summary?days=${days}`);
  }

  async getClaudeDailyCost(days = 30) {
    return this.makeRequest(`/api/claudeusage/daily-cost?days=${days}`);
  }

  async getClaudeDailyTokens(days = 30) {
    return this.makeRequest(`/api/claudeusage/daily-tokens?days=${days}`);
  }

  async getClaudeUserUsage(days = 30) {
    return this.makeRequest(`/api/claudeusage/users?days=${days}`);
  }

  async getClaudeEditDecisions(days = 30) {
    return this.makeRequest(`/api/claudeusage/edit-decisions?days=${days}`);
  }

  async getClaudeLanguages(days = 30, topN = 10) {
    return this.makeRequest(`/api/claudeusage/languages?days=${days}&topN=${topN}`);
  }

  async getClaudeActiveTime(days = 30) {
    return this.makeRequest(`/api/claudeusage/active-time?days=${days}`);
  }

  async getClaudeSourceBreakdown(days = 30) {
    return this.makeRequest(`/api/claudeusage/source-breakdown?days=${days}`);
  }

  // Secure Score Views
  async getSecureScoreViews() {
    return this.makeRequest('/api/securescoreviews');
  }

  async createSecureScoreView(data) {
    return this.makeRequest('/api/securescoreviews', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateSecureScoreView(id, data) {
    return this.makeRequest(`/api/securescoreviews/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteSecureScoreView(id) {
    return this.makeRequest(`/api/securescoreviews/${id}`, { method: 'DELETE' });
  }

  async reorderSecureScoreViews(orders) {
    return this.makeRequest('/api/securescoreviews/reorder', {
      method: 'POST',
      body: JSON.stringify({ orders }),
    });
  }

  // Secure Score Cache
  async getSecureScoreCache() {
    return this.makeRequest('/api/securescorecache');
  }

  async saveSecureScoreCache(data) {
    return this.makeRequest('/api/securescorecache', {
      method: 'POST',
      body: JSON.stringify({ data }),
    });
  }

  async getSecureScoreTicketLinks() {
    return this.makeRequest('/api/securescoreticketlinks');
  }

  async saveSecureScoreTicketLink(controlId, ticketId) {
    return this.makeRequest('/api/securescoreticketlinks', {
      method: 'POST',
      body: JSON.stringify({ controlId, ticketId }),
    });
  }

  async getAzureScoreDeltas() {
    return this.makeRequest('/api/azurescoresnapshots/deltas');
  }

  async getClaudeDailyCommits(days = 30) {
    return this.makeRequest(`/api/claudeusage/daily-commits?days=${days}`);
  }

  async getClaudeUserEditDecisions(days = 30) {
    return this.makeRequest(`/api/claudeusage/user-edit-decisions?days=${days}`);
  }

  async getClaudeUserSourceBreakdown(days = 30) {
    return this.makeRequest(`/api/claudeusage/user-source-breakdown?days=${days}`);
  }

  async downloadSupplierInvoiceFile(id) {
    const url = `${API_BASE_URL}/api/supplierinvoices/${id}/download`;
    let headers = {};
    if (authService.isAuthenticated()) {
      const token = await authService.getAccessToken();
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.blob();
  }

  // ── IT Project Tracker ────────────────────────────────────────────────────

  async getProjects(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status !== undefined && filters.status !== '') params.append('status', filters.status);
    if (filters.priority !== undefined && filters.priority !== '') params.append('priority', filters.priority);
    if (filters.type !== undefined && filters.type !== '') params.append('type', filters.type);
    if (filters.assignedTo) params.append('assignedTo', filters.assignedTo);
    if (filters.startFrom) params.append('startFrom', filters.startFrom);
    if (filters.startTo) params.append('startTo', filters.startTo);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.makeRequest(`/api/projects${query}`);
  }

  async getProjectById(id) {
    return this.makeRequest(`/api/projects/${id}`);
  }

  async createProject(data) {
    const userId = authService.getUserId();
    return this.makeRequest(`/api/projects?userId=${userId}`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateProject(id, data) {
    return this.makeRequest(`/api/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteProject(id) {
    return this.makeRequest(`/api/projects/${id}`, { method: 'DELETE' });
  }

  // Phases
  async getProjectPhases(projectId) {
    return this.makeRequest(`/api/projects/${projectId}/phases`);
  }

  async createProjectPhase(projectId, data) {
    return this.makeRequest(`/api/projects/${projectId}/phases`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async signOffPhase(projectId, phaseId) {
    const userId = authService.getUserId();
    return this.makeRequest(`/api/projects/${projectId}/phases/${phaseId}/signoff?userId=${userId}`, {
      method: 'POST',
      body: '{}'
    });
  }

  // Tasks
  async getProjectTasks(projectId, phaseId = null) {
    const query = phaseId !== null ? `?phaseId=${phaseId}` : '';
    return this.makeRequest(`/api/projects/${projectId}/tasks${query}`);
  }

  async createProjectTask(projectId, data) {
    const userId = authService.getUserId();
    return this.makeRequest(`/api/projects/${projectId}/tasks?userId=${userId}`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateProjectTask(projectId, taskId, data) {
    return this.makeRequest(`/api/projects/${projectId}/tasks/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteProjectTask(projectId, taskId) {
    return this.makeRequest(`/api/projects/${projectId}/tasks/${taskId}`, { method: 'DELETE' });
  }

  // Task Comments
  async getTaskComments(projectId, taskId) {
    return this.makeRequest(`/api/projects/${projectId}/tasks/${taskId}/comments`);
  }

  async addTaskComment(projectId, taskId, content) {
    const userId = authService.getUserId();
    return this.makeRequest(`/api/projects/${projectId}/tasks/${taskId}/comments?userId=${userId}`, {
      method: 'POST',
      body: JSON.stringify({ content })
    });
  }

  async deleteTaskComment(projectId, taskId, commentId) {
    const userId = authService.getUserId();
    return this.makeRequest(`/api/projects/${projectId}/tasks/${taskId}/comments/${commentId}?userId=${userId}`, { method: 'DELETE' });
  }

  // Budget
  async getProjectBudget(projectId) {
    return this.makeRequest(`/api/projects/${projectId}/budget`);
  }

  async addBudgetEntry(projectId, data) {
    const userId = authService.getUserId();
    return this.makeRequest(`/api/projects/${projectId}/budget?userId=${userId}`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async deleteBudgetEntry(projectId, entryId) {
    const userId = authService.getUserId();
    return this.makeRequest(`/api/projects/${projectId}/budget/${entryId}?userId=${userId}`, { method: 'DELETE' });
  }

  // Project Tickets
  async getProjectTickets(projectId) {
    return this.makeRequest(`/api/projects/${projectId}/tickets`);
  }

  async linkTicketToProjectFromProject(projectId, ticketId) {
    const userId = authService.getUserId();
    return this.makeRequest(`/api/projects/${projectId}/tickets?userId=${userId}`, {
      method: 'POST',
      body: JSON.stringify({ ticketId })
    });
  }

  async unlinkTicketFromProject(projectId, ticketId) {
    return this.makeRequest(`/api/projects/${projectId}/tickets/${ticketId}`, { method: 'DELETE' });
  }

  // Project Members
  async getProjectMembers(projectId) {
    return this.makeRequest(`/api/projects/${projectId}/members`);
  }

  async addProjectMember(projectId, data) {
    return this.makeRequest(`/api/projects/${projectId}/members`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async removeProjectMember(projectId, userId) {
    return this.makeRequest(`/api/projects/${projectId}/members/${userId}`, { method: 'DELETE' });
  }

  // Ticket-side project linking
  async getTicketProject(ticketId) {
    return this.makeRequest(`/api/tickets/${ticketId}/project`);
  }

  async linkTicketToProject(ticketId, projectId) {
    return this.makeRequest(`/api/tickets/${ticketId}/project`, {
      method: 'POST',
      body: JSON.stringify({ projectId })
    });
  }

  async unlinkTicketFromProjectByTicket(ticketId) {
    return this.makeRequest(`/api/tickets/${ticketId}/project`, { method: 'DELETE' });
  }

  // Project Export / Import
  async exportProject(projectId, projectName) {
    const headers = {};
    if (authService.isAuthenticated()) {
      const token = await authService.getAccessToken();
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/export`, { headers });
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `project-${(projectName || String(projectId)).replace(/[^a-z0-9]/gi, '_')}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async importProject(file) {
    const userId = authService.getUserId();
    const headers = {};
    if (authService.isAuthenticated()) {
      const token = await authService.getAccessToken();
      headers['Authorization'] = `Bearer ${token}`;
    }
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_BASE_URL}/api/projects/import?userId=${encodeURIComponent(userId)}`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Import failed' }));
      throw new Error(err.message || 'Import failed');
    }
    return response.json();
  }

  // Feature flag helper
  async getSystemSettingByKey(key) {
    const settings = await this.makeRequest('/api/systemsettings');
    if (Array.isArray(settings)) {
      const found = settings.find(s => s.settingKey === key);
      return found ? found.settingValue : null;
    }
    return null;
  }

  async isProjectTrackerEnabled() {
    try {
      const val = await this.getSystemSettingByKey('feature.project_tracker');
      return val === 'true';
    } catch {
      return false;
    }
  }

  async isDefenderXdrSyncEnabled() {
    try {
      const val = await this.getSystemSettingByKey('feature.defender_xdr_sync');
      return val === 'true';
    } catch {
      return false;
    }
  }

}

export const apiService = new ApiService(); 